import { useCallback, useEffect, useRef, type KeyboardEvent } from "react";
import {
  BORDER_TYPE,
  Chessboard,
  COLOR,
  INPUT_EVENT_TYPE,
} from "@vendor/cm-chessboard/src/Chessboard.js";
import { MARKER_TYPE, Markers } from "@vendor/cm-chessboard/src/extensions/markers/Markers.js";
import { PromotionDialog } from "@vendor/cm-chessboard/src/extensions/promotion-dialog/PromotionDialog.js";
import { wrapChessboard } from "../../chess/boardAdapter.ts";
import { applyBoardKey, initialBoardKeyState, type BoardKeyState } from "../../trainer/keyboard.ts";
import {
  CENTRAL_ZONE,
  computeCenterControl,
  type BoardOverlayState,
} from "../../trainer/boardOverlays.ts";
import { guideShapes, safeSquares, safeSquaresSentence } from "../../trainer/boardGuides.ts";
import { guideAvailable, guideInputFor } from "./OverlayControls.tsx";
import { clearMoveHints, isGuideMutation, paintGuideLayer, paintMoveHints } from "./guideLayer.ts";
import { getTrainer } from "../../trainer/session.ts";
import type { TrainerSnapshot } from "../../trainer/session.ts";
import { pieceSetById } from "../../appearance/appearance.ts";
import { useAppearance } from "../../appearance/useAppearance.ts";
import { watchCloseRequest } from "../layout/closeRequest.ts";

/**
 * cm-chessboard caches one piece sprite in a hidden div keyed by a fixed id.
 * Switching sets means dropping that cache so the next board fetches the new
 * sprite; otherwise every set would resolve to the first one loaded.
 */
function dropCachedPieceSprite() {
  document.getElementById("cm-chessboard-sprite")?.remove();
}

/** The sprite file the document-wide cache currently holds, across mounts. */
let cachedPieceFile: string | null = null;

const TERRITORY_MARKERS: Record<string, { class: string; slice: string }> = {};
for (let tier = 1; tier <= 5; tier += 1) {
  TERRITORY_MARKERS[`w${tier}`] = {
    class: `marker-territory-w${tier}`,
    slice: "markerSquare",
  };
  TERRITORY_MARKERS[`b${tier}`] = {
    class: `marker-territory-b${tier}`,
    slice: "markerSquare",
  };
}

type BoardHandle = {
  setPosition: (fen: string, animated?: boolean) => void;
  enableMoveInput: (handler: (event: Record<string, unknown>) => unknown, color?: string) => void;
  addMarker: (type: unknown, square: string) => void;
  removeMarkers: (type?: unknown, square?: string) => void;
  showPromotionDialog: (
    square: string,
    color: string,
    callback: (result: { piece?: string } | null) => void
  ) => void;
  destroy: () => void;
  isPromotionDialogShown: () => boolean;
  view: { svg: SVGSVGElement };
};

/**
 * Cancel an open promotion choice through the board's own cancel path (its
 * right-click handler). The dialog listens only once its opening animation
 * has finished, so a request during the animation waits for it.
 */
function cancelPromotion(board: BoardHandle, frames = 30) {
  if (!board.isPromotionDialogShown()) return;
  board.view.svg.dispatchEvent(new MouseEvent("contextmenu", { cancelable: true }));
  if (frames > 0 && board.isPromotionDialogShown())
    requestAnimationFrame(() => cancelPromotion(board, frames - 1));
}

export function TrainerBoard({
  snapshot,
  overlays,
  replySquares = null,
  onAnnounce,
}: {
  snapshot: TrainerSnapshot;
  overlays: BoardOverlayState;
  /** Squares the reply card names, rung on the board while the student asks for them. */
  replySquares?: string[] | null;
  onAnnounce: (text: string) => void;
}) {
  const appearance = useAppearance();
  const pieceSet = pieceSetById(appearance.pieces);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const boardRef = useRef<BoardHandle | null>(null);
  const keyState = useRef<BoardKeyState>(initialBoardKeyState());
  // Stops the back-button watch of an open promotion choice.
  const stopPromotionWatch = useRef<(() => void) | null>(null);
  const snapRef = useRef(snapshot);
  const overlaysRef = useRef(overlays);
  const replyRef = useRef(replySquares);
  useEffect(() => {
    snapRef.current = snapshot;
  }, [snapshot]);
  useEffect(() => {
    overlaysRef.current = overlays;
  }, [overlays]);
  useEffect(() => {
    replyRef.current = replySquares;
  }, [replySquares]);

  const submitMove = useCallback((from: string, to: string) => {
    const session = getTrainer();
    const current = snapRef.current;
    const board = boardRef.current;
    if (current.inputLocked || !board) return;
    if (!session.needsPromotion(from, to)) {
      void session.playMove({ from, to });
      return;
    }
    // Android's back button cancels the choice, as a tap off the board does.
    stopPromotionWatch.current?.();
    const stopWatching = watchCloseRequest(() => cancelPromotion(board));
    stopPromotionWatch.current = stopWatching;
    board.showPromotionDialog(
      to,
      current.playerSide === "b" ? COLOR.black : COLOR.white,
      (result) => {
        stopWatching();
        if (stopPromotionWatch.current === stopWatching) stopPromotionWatch.current = null;
        if (
          boardRef.current !== board ||
          snapRef.current.sessionId !== current.sessionId ||
          snapRef.current.fen !== current.fen
        )
          return;
        const promotion = result?.piece?.[1];
        if (promotion) void session.playMove({ from, to, promotion });
        else board.setPosition(snapRef.current.fen, true);
      }
    );
  }, []);

  const paintActorClasses = useCallback(() => {
    const root = hostRef.current;
    if (!root) return;
    root
      .querySelectorAll("g[data-square].is-lead, g[data-square].is-reserve")
      .forEach((node) => node.classList.remove("is-lead", "is-reserve"));

    const current = snapRef.current;
    const active = overlaysRef.current;
    const reserveOn = active.reserve && guideAvailable("reserve", current.mode);
    if (!active.hero && !reserveOn) return;
    const actors = current.actors ?? { hero: [], reserve: [] };
    const mark = (squares: string[], className: string) => {
      for (const square of squares) {
        const groups = root.querySelectorAll(`g[data-square="${square}"]`);
        groups.forEach((group) => {
          if (group.querySelector("use.piece")) group.classList.add(className);
        });
      }
    };
    if (active.hero) mark(actors.hero, "is-lead");
    if (reserveOn) mark(actors.reserve, "is-reserve");
  }, []);

  /** The board's view, which the guide layers draw into. */
  const moveView = useCallback(
    () =>
      (boardRef.current as (BoardHandle & { view?: Parameters<typeof paintGuideLayer>[0] }) | null)
        ?.view,
    []
  );

  /** Fills, rings, badges, arrows and lines of the safety and structure guides. */
  const paintGuides = useCallback(() => {
    const board = boardRef.current as
      (BoardHandle & { view?: Parameters<typeof paintGuideLayer>[0] }) | null;
    if (!board) return;
    const current = snapRef.current;
    const active = overlaysRef.current;
    const reply = replyRef.current;
    paintGuideLayer(
      board.view,
      guideShapes(
        {
          threatened: active.threatened,
          loose: active.loose,
          lastMove: active.lastMove,
          pins: active.pins,
          pawns: active.pawns,
          reply: Boolean(reply?.length),
        },
        { ...guideInputFor(current), replySquares: reply }
      )
    );
  }, []);

  const paintBoardOverlays = useCallback(() => {
    const board = boardRef.current;
    const root = hostRef.current;
    if (!board || !root) return;

    Object.values(TERRITORY_MARKERS).forEach((marker) => board.removeMarkers(marker));

    const current = snapRef.current;
    const active = overlaysRef.current;
    if (active.center) {
      const control = computeCenterControl(current.fen);
      for (const square of CENTRAL_ZONE) {
        const tier = control.tiers[square] || 0;
        if (tier === 0) continue;
        board.addMarker(TERRITORY_MARKERS[`${tier > 0 ? "w" : "b"}${Math.abs(tier)}`], square);
      }
    }

    paintGuides();
    paintActorClasses();
  }, [paintActorClasses, paintGuides]);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    el.innerHTML = "";
    if (cachedPieceFile && cachedPieceFile !== pieceSet.file) dropCachedPieceSprite();
    cachedPieceFile = pieceSet.file;
    const board = wrapChessboard(
      new Chessboard(el, {
        position: snapRef.current.fen,
        orientation: snapRef.current.playerSide === "b" ? COLOR.black : COLOR.white,
        assetsUrl: "/vendor/cm-chessboard/assets/",
        style: {
          cssClass: "default",
          showCoordinates: true,
          borderType: BORDER_TYPE.thin,
          aspectRatio: 1,
          pieces: { file: pieceSet.file },
        },
        extensions: [{ class: Markers }, { class: PromotionDialog }],
      })
    ) as BoardHandle;
    boardRef.current = board;
    // cm-chessboard marks its SVG role="img"; give it an accessible name.
    el.querySelector("svg")?.setAttribute("aria-label", "Chessboard");

    let actorFrame = 0;
    // The board redraws its pieces after animations and on resize; repaint
    // the piece classes and the guide layer then, ignoring our own redraws.
    const actorObserver = new MutationObserver((mutations) => {
      if (!Object.values(overlaysRef.current).some(Boolean) && !replyRef.current?.length) return;
      if (
        !mutations.some((mutation) => mutation.type === "childList" && !isGuideMutation(mutation))
      )
        return;
      cancelAnimationFrame(actorFrame);
      actorFrame = requestAnimationFrame(() => {
        paintGuides();
        paintActorClasses();
      });
    });
    actorObserver.observe(el, { childList: true, subtree: true });

    const handleMoveInput = (event: Record<string, unknown>) => {
      const session = getTrainer();
      const current = snapRef.current;
      if (current.inputLocked) return false;
      const type = String(event.type);

      if (type === INPUT_EVENT_TYPE.moveInputStarted) {
        const from = String(event.squareFrom || "");
        const dests = session.legalDestinations(from);
        if (dests.length === 0) return false;
        // With Safe squares on, the destinations are coloured by what each
        // move does to the material balance instead of plain dots.
        if (overlaysRef.current.safe) paintMoveHints(moveView(), safeSquares(current.fen, from));
        else dests.forEach((sq) => board.addMarker(MARKER_TYPE.dot, sq));
        return true;
      }

      if (type === INPUT_EVENT_TYPE.validateMoveInput) {
        board.removeMarkers(MARKER_TYPE.dot);
        clearMoveHints(moveView());
        const from = String(event.squareFrom || "");
        const to = String(event.squareTo || "");
        const dests = session.legalDestinations(from);
        if (!dests.includes(to)) return false;
        submitMove(from, to);
        return true;
      }

      if (type === INPUT_EVENT_TYPE.moveInputCanceled) {
        board.removeMarkers(MARKER_TYPE.dot);
        clearMoveHints(moveView());
        return true;
      }
      return true;
    };

    board.enableMoveInput(
      handleMoveInput,
      snapRef.current.playerSide === "b" ? COLOR.black : COLOR.white
    );

    return () => {
      actorObserver.disconnect();
      cancelAnimationFrame(actorFrame);
      // A board torn down mid-choice never answers: stop watching for back.
      stopPromotionWatch.current?.();
      stopPromotionWatch.current = null;
      board.destroy();
      boardRef.current = null;
    };
  }, [paintActorClasses, paintGuides, moveView, snapshot.slug, pieceSet.file, submitMove]);

  useEffect(() => {
    boardRef.current?.setPosition(snapshot.fen, true);
    keyState.current = initialBoardKeyState(snapshot.playerSide === "b" ? "e7" : "e2");
    boardRef.current?.removeMarkers(MARKER_TYPE.frame);
    clearMoveHints(moveView());
  }, [snapshot.fen, snapshot.playerSide, moveView]);

  useEffect(() => {
    paintBoardOverlays();
    const frame = requestAnimationFrame(paintBoardOverlays);
    const afterAnimation = window.setTimeout(paintBoardOverlays, 380);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(afterAnimation);
    };
    // history is intentionally included: actor identities follow the move log.
  }, [
    snapshot.fen,
    snapshot.history,
    snapshot.openingId,
    overlays,
    replySquares,
    pieceSet.file,
    paintBoardOverlays,
  ]);

  const paintSelection = (state: BoardKeyState) => {
    const board = boardRef.current;
    if (!board) return;
    board.removeMarkers(MARKER_TYPE.frame);
    board.addMarker(MARKER_TYPE.frame, state.focused);
    if (state.selected) board.addMarker(MARKER_TYPE.frame, state.selected);
    if (state.selected && overlays.safe && !snapshot.inputLocked)
      paintMoveHints(moveView(), safeSquares(snapshot.fen, state.selected));
    else clearMoveHints(moveView());
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (snapshot.inputLocked) return;
    const result = applyBoardKey(keyState.current, event.key, snapshot.playerSide);
    keyState.current = result.state;
    if (
      event.key.startsWith("Arrow") ||
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "Escape"
    ) {
      event.preventDefault();
    }
    paintSelection(result.state);
    if (result.action?.type === "announce") {
      // Selecting a piece with Safe squares on also says where it is safe.
      const safety =
        overlays.safe && result.state.selected && result.action.text.startsWith("Selected")
          ? safeSquaresSentence(snapshot.fen, result.state.selected)
          : "";
      onAnnounce(safety ? `${result.action.text}. ${safety}` : result.action.text);
    }
    if (result.action?.type === "move") {
      submitMove(result.action.from, result.action.to);
    }
  };

  return (
    <div
      className="board-wrap trainer-board-wrap"
      tabIndex={0}
      role="application"
      aria-label="Chessboard. Arrow keys move the cursor. Enter selects a square."
      data-input-locked={snapshot.inputLocked ? "true" : "false"}
      data-piece-set={pieceSet.id}
      data-player-side={snapshot.playerSide}
      onKeyDown={onKeyDown}
    >
      <div id="board" className="board" ref={hostRef} />
    </div>
  );
}
